/**
 * What the Data Room card on `startup-detail.tsx` has to SAY — and whether it
 * draws a button of its own at all.
 *
 * A leaf module: its only import is `./stage`, which imports nothing. So the
 * one question this screen kept getting wrong can be tested without rendering
 * anything (`@testing-library/react-native` cannot load on this branch — a
 * react-test-renderer version mismatch — so a claim about what renders has to
 * be provable some other way, and inline JSX conditions are not provable at
 * all). Same shape and the same reason as `stage-change.ts`.
 *
 * WHY IT EXISTS
 * -------------
 * This screen used to render TWO buttons both labelled "Request Access" — the
 * derived action bar's and this card's — doing DIFFERENT things: one wrote the
 * stage without asking the founder, the other asked the founder without moving
 * the stage. Each did half the job, and the deal stranded either way.
 *
 * They became one action, and the card yields to the action bar. But the test
 * for "yield" was `!requestOfferedInActionBar`, which conflated two unrelated
 * situations: the bar is offering the request (yield — the deal is fine), and
 * the request is not a legal move at all (do not offer it — the deal has
 * ENDED). While `screening` did not offer `request-access` the second case was
 * invisible, because the default stage fell into the first. Now that it does,
 * the only stages left in that gap are the three endings — where the button
 * used to ask a founder for documents and then try to drag a terminal deal
 * back to `awaiting_access`.
 */
import { offeredActions, type DealStage } from './stage';

/**
 * The card's position, in the order the card resolves it. Exactly one holds.
 *
 * `open` and `awaiting` outrank `unclaimed` and each other in this order
 * because they describe what has already HAPPENED; the rest describe what may
 * happen next.
 */
export type DataRoomCardState =
  /** The documents are open: the card's button becomes "View Data Room". */
  | 'open'
  /** No founder has claimed the profile, so a request could never be answered. */
  | 'unclaimed'
  /** The request is in and the founder has not replied. */
  | 'awaiting'
  /**
   * The founder said no. The card turns red and offers a way to MESSAGE them,
   * never to ask again — a rejection is final and only the founder can reopen
   * it.
   *
   * Named `access-rejected`, not `rejected`, because `DealStage` already has a
   * `rejected` meaning the opposite party: the INVESTOR passed on the startup.
   * Two unrelated rejections in one module is exactly the kind of collision
   * this file exists to prevent.
   */
  | 'access-rejected'
  /** The derived action bar is offering the request — the card must NOT draw a second button. */
  | 'ask-in-bar'
  /** The deal has ended. Requesting is not a legal move, so nothing is offered. */
  | 'concluded';

export interface DataRoomCardInput {
  /** `dataRoomOpen(stage)` — are the founder's documents readable now? */
  documentsOpen: boolean;
  /** No claimed founder profile behind this company. */
  unclaimed: boolean;
  /** `awaitingFounderAnswer(stage)`, OR the optimistic just-asked local flag. */
  awaitingFounder: boolean;
  /**
   * `requestStateFor(...) === 'rejected'` — the founder answered no.
   *
   * Read from the REQUEST ROW, not the stage. `respond_to_data_room_request`
   * deliberately leaves the stage alone ("a founder cannot evict a deal from
   * someone else's pipeline"), so the stage still says `awaiting_access` long
   * after the answer arrived. Every stage-derived predicate therefore reports
   * "still waiting", which is how the card kept re-offering a request that RLS
   * refuses. Optional so existing callers keep compiling as not-rejected.
   */
  requestRejected?: boolean;
  /** Is `request-access` among the actions the derived bar is rendering? */
  requestOfferedInActionBar: boolean;
}

export function dataRoomCardState(input: DataRoomCardInput): DataRoomCardState {
  if (input.documentsOpen) return 'open';
  if (input.unclaimed) return 'unclaimed';
  // BEFORE `awaiting`: both can be true at once (the stage says awaiting, the
  // row says rejected) and the row is the newer, truer fact.
  if (input.requestRejected) return 'access-rejected';
  if (input.awaitingFounder) return 'awaiting';
  if (input.requestOfferedInActionBar) return 'ask-in-bar';
  return 'concluded';
}

/**
 * Does the card draw a button of its own?
 *
 * False for BOTH states that offer nothing, and they are different refusals:
 * `ask-in-bar` means someone else is offering it, `concluded` means nobody may.
 * Collapsing them back into one boolean is how the terminal case hid.
 */
export function cardDrawsOwnCta(state: DataRoomCardState): boolean {
  return (
    state === 'open' ||
    state === 'unclaimed' ||
    state === 'awaiting' ||
    // Draws, but the button is a way to REACH the founder, not to ask again.
    state === 'access-rejected'
  );
}

/**
 * Is the derived action bar offering `request-access` at this stage?
 *
 * The SAME derivation `startup-detail.tsx` performs — `offeredActions(stage)`
 * filtered by the unclaimed rule — expressed once so a test can ask the real
 * ACTIONS table rather than a transcription of it. An unclaimed profile drops
 * the action because a request written against it can never be responded to.
 */
export function requestOfferedAt(stage: DealStage, unclaimed: boolean): boolean {
  return !unclaimed && offeredActions(stage).includes('request-access');
}
